import { isMessageMentioningUser, stripMessageHtml } from './mentionUtils';
import { formatReactionToMessageLine } from './reactionConfig';
import { isDisplayableImage } from './fileUtils';

/**
 * Notification kinds — extend this enum when adding new FAB / push types.
 */
export const NOTIFICATION_KIND = {
  CHANNEL_MESSAGE: 'channel_message',
  CHANNEL_THREAD_REPLY: 'channel_thread_reply',
  DIRECT_MESSAGE: 'direct_message',
  DIRECT_THREAD_REPLY: 'direct_thread_reply',
  CHANNEL_MENTION: 'channel_mention',
  DIRECT_MENTION: 'direct_mention',
  PANEL_ACCESS_REQUEST: 'panel_access_request',
  PANEL_ACCESS_GRANTED: 'panel_access_granted',
  PANEL_ACCESS_APPROVED: 'panel_access_approved',
  PANEL_ACCESS_REJECTED: 'panel_access_rejected',
  PANEL_ACCESS_REVOKED: 'panel_access_revoked',
  TASK_DELEGATED: 'task_delegated',
  USER_NOTIFICATION: 'user_notification',
};

const DEFAULT_SENDER = 'Someone';
const SENT_A_FILE = 'sent a file';
const SENT_IMAGE = 'sent an image';
const SENT_LINK = 'sent a link';

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/i;
const WWW_IN_TEXT = /\bwww\.[^\s<>"']+/i;

function messageLooksLikeLink(message) {
  if (message?.linkPreview?.url) return true;
  const type = String(message?.type || '').toUpperCase();
  if (type === 'LINK') return true;
  const html = String(message?.content || '');
  if (/<a\s/i.test(html)) return true;
  const text = stripMessageHtml(html);
  return URL_IN_TEXT.test(text) || WWW_IN_TEXT.test(text);
}

export function getFileNotificationParts(message) {
  const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
  const type = String(message?.type || '').toUpperCase();
  const hasFile = attachments.length > 0 || type === 'IMAGE' || type === 'FILE';
  const image = attachments.find((item) => isDisplayableImage(item)) || null;
  const isImage = Boolean(image) || type === 'IMAGE';
  const isLink = !hasFile && messageLooksLikeLink(message);
  let preview = '';
  if (isImage) preview = SENT_IMAGE;
  else if (hasFile) preview = SENT_A_FILE;
  else if (isLink) preview = SENT_LINK;
  return {
    hasFile,
    isImage,
    isLink,
    thumbnailFileKey: image?.fileUrl || image?.url || null,
    preview,
  };
}

function eventPublishPreview(message) {
  const name = String(message?.eventInfo?.name || '').trim();
  if (!name) return '';
  const type = String(message?.eventInfo?.eventType || '').replace(/_/g, ' ').toLowerCase();
  return type ? `${type}: ${name}` : name;
}

export function formatSenderColonPreview(sender, preview) {
  const who = String(sender || '').trim();
  const text = String(preview || '').trim();
  if (!who) return text;
  if (!text) return who;
  const lowerWho = who.toLowerCase();
  const lowerText = text.toLowerCase();
  if (
    lowerText === lowerWho
    || lowerText.startsWith(`${lowerWho} `)
    || lowerText.startsWith(`${lowerWho}:`)
  ) {
    return text;
  }
  return `${who}: ${text}`;
}

export function formatAttendanceSpokenLine({
  name,
  eventType,
  reason,
  spoken,
  notificationTitle,
} = {}) {
  const who = String(name || '').trim() || 'Someone';
  const type = String(eventType || '').trim().toUpperCase();
  const note = String(reason || '').trim();
  const notePart = note && note.toLowerCase() !== 'short break' ? ` · ${note}` : '';
  if (type === 'CHECK_OUT') return `${who} logged out`;
  if (type === 'CHECK_IN' || type === 'RELOGIN') return `${who} logged in`;
  if (type === 'BREAK_START') return `${who} started a short break${notePart}`;
  if (type === 'BREAK_END') return `${who} joined back from break`;
  if (type === 'LEAVE_APPLY' || type === 'LEAVE_APPROVED') return `${who} is on leave`;
  const fallback = String(spoken || notificationTitle || '').trim();
  if (fallback && fallback.toLowerCase() !== who.toLowerCase()) return fallback;
  return who;
}

export function getMessageContentPreview(message, maxLength = 60) {
  if (!message) return '';
  const file = getFileNotificationParts(message);
  if (file.preview) return file.preview;
  let preview = '';
  if (message.content) {
    preview = stripMessageHtml(message.content);
    if (preview.length > maxLength) {
      preview = `${preview.substring(0, maxLength)}...`;
    }
  }
  if (!preview) {
    preview = eventPublishPreview(message);
    if (preview.length > maxLength) {
      preview = `${preview.substring(0, maxLength)}...`;
    }
  }
  return preview || 'New message';
}

export function spokenChatLine(message, senderName) {
  const sender = senderName?.trim() || message?.senderName?.trim() || DEFAULT_SENDER;
  const file = getFileNotificationParts(message);
  if (file.preview) return `${sender} ${file.preview}`;
  if (isThreadReply(message)) {
    const preview = stripMessageHtml(message?.content || '').trim();
    if (!preview) return `${sender} replied in thread`;
    const short = preview.length > 60 ? `${preview.substring(0, 60)}...` : preview;
    return `${sender} replied in thread: ${short}`;
  }
  const eventLine = eventPublishPreview(message);
  if (eventLine) return `${sender} published ${eventLine}`;
  return '';
}

export function buildMessageHighlightToast(message, {
  eventType = 'MESSAGE',
  channelName = '',
  isDirectMessage = false,
} = {}) {
  const file = getFileNotificationParts(message);
  const text = stripMessageHtml(message?.content || '').trim();
  if (message?.attendanceInfo) {
    const line = formatAttendanceSpokenLine({
      name: message.senderName,
      eventType: message.attendanceInfo.eventType,
      reason: message.attendanceInfo.eventNote,
      spoken: text,
    });
    return {
      eventType: 'ATTENDANCE_EVENT',
      title: line,
      body: line,
      actorName: '',
      senderId: message?.senderId,
      ticketTitle: channelName,
      sourceChannelId: message?.channelId,
      sourceMessageId: message?.id,
      thumbnailFileKey: null,
    };
  }
  const spoken = spokenChatLine(message);
  const body = file.preview || text || 'New message';
  return {
    eventType,
    title: eventType === 'MENTION' ? 'Mention' : (isDirectMessage ? 'Direct message' : channelName),
    body: file.preview ? spoken : body,
    actorName: file.preview ? '' : message?.senderName,
    senderId: message?.senderId,
    ticketTitle: channelName,
    sourceChannelId: message?.channelId,
    sourceMessageId: message?.id,
    thumbnailFileKey: file.thumbnailFileKey,
  };
}

export function isThreadReply(message) {
  return !!(message?.replyToMessageId);
}

/**
 * Resolve notification kind from message + conversation context.
 */
export function resolveNotificationKind(message, { isDirectMessage = false, isMentioned = false } = {}) {
  if (isMentioned) {
    return isDirectMessage ? NOTIFICATION_KIND.DIRECT_MENTION : NOTIFICATION_KIND.CHANNEL_MENTION;
  }
  if (isThreadReply(message)) {
    return isDirectMessage
      ? NOTIFICATION_KIND.DIRECT_THREAD_REPLY
      : NOTIFICATION_KIND.CHANNEL_THREAD_REPLY;
  }
  return isDirectMessage ? NOTIFICATION_KIND.DIRECT_MESSAGE : NOTIFICATION_KIND.CHANNEL_MESSAGE;
}

/**
 * Format title + body for chat notifications (FAB bubble, desktop, etc.).
 *
 * @param {object} options
 * @param {object} options.message
 * @param {string} [options.channelName]
 * @param {boolean} [options.isDirectMessage]
 * @param {boolean} [options.isMentioned]
 * @param {number} [options.maxBodyLength]
 * @returns {{ kind: string, title: string, body: string }}
 */
export function formatChatNotification({
  message,
  channelName = '',
  isDirectMessage = false,
  isMentioned = false,
  maxBodyLength = 60,
}) {
  const sender = message?.senderName?.trim() || DEFAULT_SENDER;
  const channel = channelName?.trim() || (isDirectMessage ? '' : 'Channel');
  const preview = getMessageContentPreview(message, maxBodyLength);
  const kind = resolveNotificationKind(message, { isDirectMessage, isMentioned });
  const spoken = spokenChatLine(message, sender);
  const withSpoken = (payload) => (spoken ? { ...payload, spoken } : payload);

  switch (kind) {
    case NOTIFICATION_KIND.CHANNEL_MENTION:
      return withSpoken({
        kind,
        title: `${sender} mentioned you in ${channel}`,
        body: preview,
      });

    case NOTIFICATION_KIND.DIRECT_MENTION:
      return withSpoken({
        kind,
        title: sender,
        body: preview ? `mentioned you · ${preview}` : 'mentioned you',
      });

    case NOTIFICATION_KIND.CHANNEL_THREAD_REPLY:
      return withSpoken({
        kind,
        title: channel,
        body: preview && preview !== 'New message'
          ? `${sender} replied in thread: ${preview}`
          : `${sender} replied in thread`,
      });

    case NOTIFICATION_KIND.DIRECT_THREAD_REPLY:
      return withSpoken({
        kind,
        title: sender,
        body: preview && preview !== 'New message'
          ? `${sender} replied in thread: ${preview}`
          : `${sender} replied in thread`,
      });

    case NOTIFICATION_KIND.DIRECT_MESSAGE:
      return withSpoken({
        kind,
        title: sender,
        body: preview,
      });

    case NOTIFICATION_KIND.CHANNEL_MESSAGE:
    default:
      return withSpoken({
        kind,
        title: channel,
        body: formatSenderColonPreview(sender, preview),
      });
  }
}

/**
 * Desktop / browser notification variant (slightly different title phrasing).
 */
export function formatDesktopNotification({
  message,
  channelName = '',
  isDirectMessage = false,
  isMentioned = false,
}) {
  const formatted = formatChatNotification({
    message,
    channelName,
    isDirectMessage,
    isMentioned,
    maxBodyLength: 100,
  });

  const sender = message?.senderName?.trim() || DEFAULT_SENDER;
  const channel = channelName?.trim() || 'Channel';

  if (formatted.kind === NOTIFICATION_KIND.DIRECT_MESSAGE) {
    return { ...formatted, title: `New message from ${sender}` };
  }
  if (formatted.kind === NOTIFICATION_KIND.CHANNEL_MESSAGE) {
    return { ...formatted, title: `${sender} in ${channel}` };
  }
  if (formatted.kind === NOTIFICATION_KIND.CHANNEL_THREAD_REPLY) {
    return { ...formatted, title: `${sender} in ${channel}` };
  }
  if (formatted.kind === NOTIFICATION_KIND.DIRECT_THREAD_REPLY) {
    return { ...formatted, title: sender };
  }

  return formatted;
}

/**
 * Latest unread message from another user in a channel (newest first).
 */
export function getLastUnreadMessageInChannel(channelMessages, userId, lastViewedIso) {
  if (!channelMessages?.length || !userId) return null;

  const lastViewedTime = lastViewedIso
    ? new Date(lastViewedIso).getTime()
    : 0;

  for (let i = channelMessages.length - 1; i >= 0; i--) {
    const msg = channelMessages[i];
    const msgTime = new Date(msg.createdAt || msg.timestamp || 0).getTime();
    const isFromOtherUser = msg.senderId !== userId;
    const isUnread = lastViewedTime === 0 || msgTime > lastViewedTime;

    if (isFromOtherUser && isUnread) {
      return msg;
    }
  }
  return null;
}

/**
 * Build FAB preview for the last unread message in a conversation.
 */
export function getUnreadConversationPreview({
  channelId,
  channelName,
  isDirectMessage,
  messages,
  userId,
  username,
  lastViewed,
}) {
  const channelMessages = messages[channelId] || [];
  const lastUnread = getLastUnreadMessageInChannel(channelMessages, userId, lastViewed[channelId]);
  if (!lastUnread) return null;

  const isMentioned = isMessageMentioningUser(lastUnread, userId, username);

  return formatChatNotification({
    message: lastUnread,
    channelName,
    isDirectMessage,
    isMentioned,
    maxBodyLength: isDirectMessage ? 60 : 50,
  });
}

/**
 * Format panel-access notification for FAB / desktop.
 *
 * @param {{ eventType: string, request: object }} event
 * @returns {{ kind: string, title: string, body: string }}
 */
export function formatPanelAccessNotification({ eventType, request }) {
  const requesterName = request?.requesterName?.trim() || 'Someone';
  const targetName = request?.targetUserName?.trim() || 'Someone';
  const kind = (eventType || '').toLowerCase();

  switch (eventType) {
    case 'PANEL_ACCESS_REQUEST':
      return {
        kind: NOTIFICATION_KIND.PANEL_ACCESS_REQUEST,
        title: 'Panel access request',
        body: `${requesterName} wants to view your daily tasks panel`,
      };
    case 'PANEL_ACCESS_GRANTED':
      return {
        kind: NOTIFICATION_KIND.PANEL_ACCESS_GRANTED,
        title: 'Panel access granted',
        body: `${targetName} shared their panel with you`,
      };
    case 'PANEL_ACCESS_APPROVED':
      return {
        kind: NOTIFICATION_KIND.PANEL_ACCESS_APPROVED,
        title: 'Panel access approved',
        body: `${targetName} approved your panel access request`,
      };
    case 'PANEL_ACCESS_REJECTED':
      return {
        kind: NOTIFICATION_KIND.PANEL_ACCESS_REJECTED,
        title: 'Panel access declined',
        body: `${targetName} declined your panel access request`,
      };
    case 'PANEL_ACCESS_REVOKED':
      return {
        kind: NOTIFICATION_KIND.PANEL_ACCESS_REVOKED,
        title: 'Panel access revoked',
        body: 'Panel access was revoked',
      };
    default:
      return {
        kind: kind || 'panel_access',
        title: 'Panel access update',
        body: 'Your panel access status changed',
      };
  }
}

/**
 * Spoken/display line for task and workflow assignment — never includes the ticket title.
 */
export function formatAssignedTaskCheckOnce(actorName, { created = false } = {}) {
  const name = actorName?.trim() || DEFAULT_SENDER;
  const action = created
    ? `${name} created a task for you`
    : `${name} assigned a task to you`;
  return {
    title: action,
    body: 'Kindly check once',
    line: `${action}. Kindly check once.`,
  };
}

export function formatReminderTriggeredNotification() {
  return {
    title: 'You have a reminder',
    body: 'Please check',
    line: 'You have a reminder. Please check.',
  };
}

/**
 * Format task delegation notification for FAB / desktop.
 *
 * @param {{ task: object, delegatorName?: string }} event
 * @returns {{ kind: string, title: string, body: string }}
 */
export function formatTaskDelegatedNotification({ task, delegatorName, assignmentKind }) {
  const created = assignmentKind === 'CREATED';
  const spoken = formatAssignedTaskCheckOnce(delegatorName, { created });
  return {
    kind: NOTIFICATION_KIND.TASK_DELEGATED,
    title: spoken.title,
    body: spoken.body,
    line: spoken.line,
  };
}

export function formatUserNotification(event) {
  const eventType = String(event?.eventType || '').toUpperCase();
  if (eventType === 'TASK_ASSIGNED' || eventType === 'WORKFLOW_ASSIGNED') {
    const spoken = formatAssignedTaskCheckOnce(event?.actorName, {
      created: eventType === 'WORKFLOW_ASSIGNED',
    });
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title: spoken.title,
      body: spoken.body,
      line: spoken.line,
    };
  }
  if (eventType === 'REMINDER') {
    const spoken = formatReminderTriggeredNotification();
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title: spoken.title,
      body: spoken.body,
      line: spoken.line,
      spoken: spoken.line,
    };
  }
  if (eventType === 'LEAVE_APPROVAL_REQUEST') {
    const title = event?.title?.trim() || `${event?.actorName || 'Someone'} requested leave approval`;
    const body = event?.body?.trim() || event?.label?.trim() || 'Open Team Attendance to approve or reject.';
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title,
      body,
      line: body && body !== title ? `${title}. ${body}` : title,
      spoken: body && body !== title ? `${title}. ${body}` : title,
    };
  }
  if (eventType === 'EVENT_PUBLISHED') {
    const title = event?.title?.trim() || 'New event';
    const body = event?.body?.trim() || event?.label?.trim() || title;
    const line = body && body !== title ? `${title}. ${body}` : title;
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title,
      body,
      line,
      spoken: line,
    };
  }
  if (eventType === 'MEETING_INVITE' || eventType === 'MEETING_UPDATED'
      || eventType === 'MEETING_CANCELLED' || eventType === 'MEETING_RSVP') {
    const title = event?.title?.trim()
      || (eventType === 'MEETING_INVITE'
        ? 'Meeting invite'
        : eventType === 'MEETING_UPDATED'
          ? 'Meeting updated'
          : eventType === 'MEETING_CANCELLED'
            ? 'Meeting cancelled'
            : 'Meeting response');
    const body = event?.body?.trim() || event?.label?.trim() || event?.ticketTitle || title;
    const line = body && body !== title ? `${title}. ${body}` : title;
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title,
      body,
      line,
      spoken: line,
    };
  }
  const title = event?.title?.trim() || 'Notification';
  if (eventType === 'MESSAGE_REACTION') {
    const bodyText = event?.body?.trim() || '';
    const toIndex = bodyText.lastIndexOf(' to ');
    const preview = toIndex >= 0 ? bodyText.slice(toIndex + 4).trim() : (event?.ticketTitle || '');
    return {
      kind: NOTIFICATION_KIND.USER_NOTIFICATION,
      title,
      body: formatReactionToMessageLine(
        event?.reactionKey || event?.reactionEmoji || event?.statusLabel,
        preview,
        bodyText || title
      ),
    };
  }
  const body = event?.body?.trim() || event?.label?.trim() || title;
  return {
    kind: NOTIFICATION_KIND.USER_NOTIFICATION,
    title,
    body,
  };
}
